import { LoadingOutlined } from '@ant-design/icons';
import { QueryClient } from '@tanstack/react-query';
import { Divider, Menu, Tooltip } from 'antd';
import dayjs from 'dayjs';
import { GetServerSideProps } from 'next';
import Head from 'next/head';
import Image from 'next/image';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { getSession, useSession } from 'next-auth/react';
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { EyeOnSvg } from '../../../../assets/icons';
import DownloadSvg from '../../../../assets/icons/DownloadSvg';
import GroupSvg from '../../../../assets/icons/GroupSvg';
import PlaySvg from '../../../../assets/icons/PlaySvg';
import ProfileChecked from '../../../../assets/icons/ProfileChecked';
import ShareSvg from '../../../../assets/icons/ShareSvg';
import coverImage from '../../../../assets/images/app/emptyTemplateCoverPhoto.png';
import theme from '../../../../assets/theme';
import { Button } from '../../../../components/_atoms/Button';
import DropDownButton, {
  DropdownButtonItem,
} from '../../../../components/_atoms/DropdownButton/DropdownButton';
import { IconWrapper } from '../../../../components/_atoms/IconWrapper';
import BaseModal from '../../../../components/_molecules/BaseModal';
import Component404 from '../../../../components/_molecules/Component404';
import { NewLandingPageFooter } from '../../../../components/_molecules/LandingPageFooter';
import ShareGlobalLesson from '../../../../components/_molecules/ShareGlobalLesson';
import LessonCard from '../../../../components/_organisms/LessonCard';
import { SlideWrapperOverlay } from '../../../../components/_organisms/LiveGame';
import LiveGamePreviewActionButtons from '../../../../components/_organisms/LivePreviewActionButtons';
import { PREVIEW_CONTAINER_ID } from '../../../../components/_organisms/LivePreviewActionButtons/LivePreviewActionButtons';
import NewAppLayout from '../../../../components/layouts/NewAppLayout';
import { ProfileCircle } from '../../../../components/ProfileCircle';
import { Slide } from '../../../../components/SlideComponents/slide.types';
import SlideWrapperViwer from '../../../../components/SlideComponents/SlideWrapperViewer';
import useAddToMyLessons from '../../../../hooks/useAddToMyLessons';
import { useBreakPoint } from '../../../../hooks/useBreakPoint';
import { useDiscoverSearchQueryState } from '../../../../hooks/useDiscoverSearchQueryState';
import useIndexedTemplate from '../../../../hooks/useIndexedTemplate';
import { LessonMediaPreFetcher } from '../../../../hooks/useMediaPreFetcher';
import { useMetrics } from '../../../../hooks/useMetrics';
import usePresentPublicLesson from '../../../../hooks/usePresentPublicLesson';
import usePreviewGame from '../../../../hooks/usePreviewGame';
import useRouterMatch from '../../../../hooks/useRouterMatch';
import { useTemplateCreatorPhoto } from '../../../../hooks/useTemplateCreatorPhoto';
import useTemplateSearch, {
  BASE_TEMPLATE_SEARCH_LIMIT,
} from '../../../../hooks/useTemplateSearch';
import backendService from '../../../../services/backendService';
import {
  DiscoverSearchQuery,
  PreviewTemplate,
} from '../../../../services/backendService/types';
import DiscoverService from '../../../../services/DiscoverService/DiscoverService';
import {
  getDiscoverCreatorUrl,
  getDiscoverLessonUrl,
} from '../../../../services/DiscoverService/discoverUtils';
import Metrics from '../../../../services/metrics';
import { getBaseUrl } from '../../../../utils/client';
import { detectAndBestEffortSetLanguage } from '../../../../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../../../../utils/react-query-utils';
import { provisionFlags } from '../../../../utils/ServerSideFeatureFlagUtils';
import { CoverPhotoRenderer } from '../../index_deprecated';

export const TEMPLATE_SUGGESTED_CONTENT_CACHE_KEY = 'template-suggested-content';

const LessonSavedContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 14px;
`;

const PageContainer = styled.div<{ $isAuthenticated: boolean }>`
  position: relative;
  width: 100%;
  height: 100%;
  background: ${theme.colors.background};
  @media (min-width: 800px) {
    padding-left: ${({ $isAuthenticated }) => ($isAuthenticated ? '240px' : '0px')};
  }
`;

const MainContainer = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 300px auto;
  padding-top: 60px;
  position: relative;
  @media (max-width: 800px) {
    grid-template-columns: auto;
  }
`;

const ContentContainer = styled.div`
  width: 100%;
  padding: 20px;
  flex: 1 1;
`;
const TemplateInfoContainer = styled.div`
  width: fit-content;
  margin: 0 auto;
  padding: 20px;
  display: grid;
  grid-template-columns: minmax(auto, 500px) 500px;
  gap: 10px;

  @media (max-width: 1000px) {
    grid-template-columns: minmax(auto, 500px) 400px;
  }
  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const PreviewContainer = styled.div<{ $isLoading?: boolean }>`
  width: 100%;
  aspect-ratio: 16/9;
  border-radius: 10px;
  box-shadow: 0px 2px 20px rgba(0, 0, 0, 20%);
  overflow: hidden;
`;

const ImageContainer = styled.div`
  width: 100%;
  max-height: 350px;
  height: fit-content;

  img {
    width: 100%;
    height: 100%;
    overflow: hidden;
    object-fit: cover;
  }
`;

const ProfileSection = styled.div`
  display: flex;
  padding-left: 5px;
  padding-right: 5px;
  box-shadow: none;
  border: none;
  justify-content: flex-start;
  width: 100%;
  align-items: center;
  margin: 20px 0px;
  a {
    &:hover {
      text-decoration: underline;
    }
  }

  h2 {
    display: flex;
    align-items: center;
  }

  span {
    display: flex;
    align-items: baseline;
    padding-right: 6px;
  }

  h2,
  p {
    margin: 0px 0px 0px 20px;
    font-size: 14px;
  }

  p {
    color: ${theme.colors.text.dark};
  }

  @media (max-width: 900px) {
    p {
      font-size: 11px;
    }
  }
`;

const LessonHead = () => {
  const { template, isError } = useContext(TemplateContext);
  const router = useRouter();
  if (isError) return <Component404 />;
  if (!template) return null;
  return (
    <Head>
      <title>
        {template.title} by {template.creatorName} - Curipod
      </title>
      <meta
        property="og:title"
        content={`${template.title} by ${template.creatorName} - Curipod`}
        key="title"
      />
      <meta
        name="twitter:title"
        content={`${template.title} by ${template.creatorName} - Curipod`}
      />
      <meta
        name="description"
        content={`${template.description} - Curipod` || ''}
        key="namedescription"
      />
      <meta
        property="description"
        content={`${template.description} - Curipod` || ''}
        key="description"
      />
      <meta
        property="og:description"
        content={`${template.description}` || ''}
        key="og:description"
      />
      <meta name="twitter:description" content={`${template.description}` || ''} />
      <meta
        property="og:image"
        content={backendService.instance.discover.getTemplateThumbnailUrl(
          template.coverImageId || '',
        )}
        key="imageurl"
      />
      <meta
        property="og:image:secure_url"
        content={backendService.instance.discover.getTemplateThumbnailUrl(
          template.coverImageId || '',
        )}
        key="imagesecureurl"
      />
      <meta property="og:type" content="website" />

      <meta property="og:url" content={`${getBaseUrl()}${router.asPath}`} key="ogurl" />
      <meta property="og:image:type" content={'image/jpeg'} key="imagetype" />
      <meta property="og:image:alt" content={template.description || ''} key="imageAlt" />
      <meta property="og:image:width" content={'300'} key="imageWidth" />
      <meta property="og:image:height" content={'190'} key="imageHeight" />
      <meta
        name="twitter:image"
        content={backendService.instance.discover.getTemplateThumbnailUrl(
          template.coverImageId || '',
        )}
      />
      <meta name="twitter:site" content={'@curipodofficial'} />
      <meta name="twitter:card" content={'summary'} />
      <meta name="twitter:widgets:theme" content="light" />
      <meta name="twitter:url" content={`${getBaseUrl()}${router.asPath}`} />
    </Head>
  );
};

const LessonDataFetcher = ({
  templateId,
  children,
}: {
  templateId: string;
  children: React.ReactNode;
}) => {
  return (
    <IndexedLessonDataFetcher templateId={templateId}>
      <LessonHead />
      <>{children}</>
    </IndexedLessonDataFetcher>
  );
};

const TemplateContext = React.createContext<{
  template?: PreviewTemplate;
  isError: boolean;
}>({
  template: undefined,
  isError: false,
});

const IndexedLessonDataFetcher = ({
  templateId,
  children,
}: {
  templateId: string;
  children: React.ReactNode;
}) => {
  const { indexedTemplate: template, isError } = useIndexedTemplate(templateId);

  const previewTemplate = useMemo(() => {
    if (!template) return undefined;
    const previewTemplate: PreviewTemplate = {
      id: template.id,
      creatorThumbnailId: template.creatorThumbnailId,
      creatorId: template.creatorId,
      creatorProfileId: template.creatorProfileId,
      slides: template.slides || [],
      coverImageId: template.coverImageId,
      coverImageAltText: template.coverImageAltText,
      title: template.title,
      creatorName: template.creatorName || '-',
      modifiedAt: template.modifiedAt,
      description: template.description,
      tenantId: template.tenantId,
      activeGame: undefined,
      isPrivate: false,
      language: template.language,
      playCount: template.playCount,
      downloadCount: template.downloadCount,
    };
    return previewTemplate;
  }, [template]);
  return (
    <TemplateContext.Provider value={{ template: previewTemplate, isError: isError }}>
      {children}
    </TemplateContext.Provider>
  );
};

const presentMenu = ({
  title,
  description,
  onClick,
  icon,
}: {
  title: string;
  description: string;
  onClick: () => void;
  icon?: ReactNode;
}) => (
  <Menu>
    <Menu.Item key="openpreview" onClick={onClick}>
      <DropdownButtonItem
        isDarkTheme={false}
        icon={<IconWrapper>{icon ? icon : <EyeOnSvg />}</IconWrapper>}
        title={title}
        description={description}
      />
    </Menu.Item>
  </Menu>
);

const AuthenticatedActions = ({ template }: { template: PreviewTemplate }) => {
  const { isLoadingPreview, createPreviewGame } = usePreviewGame(!template.isPrivate);
  const { t } = useTranslation('Host');
  const isDesktop = useBreakPoint(800);
  const metrics = useMetrics();
  const { tenantId, router } = useRouterMatch();

  const { presentPublicLesson, isCreatingGame } = usePresentPublicLesson();

  const { addToMyLessons, isLoading, data } = useAddToMyLessons();
  const handleEdit = () => {
    metrics.logEvent('Preview.Lesson.Edit', {
      isPrivate: template.isPrivate,
    });
    Metrics.incrementLessonsDownloaded();

    if (!isLoading && tenantId) {
      if (data) {
        router.push(`/${tenantId}/lessons/${data?.id}/edit`);
      } else {
        addToMyLessons({
          templateId: template.id,
          activeTenantId: tenantId,
          onSuccessFunction: data => router.push(`/${tenantId}/lessons/${data?.id}/edit`),
        });
      }
    }
  };

  const handlePresent = () => {
    if (template.slides) {
      metrics.logEvent('Preview.Lesson.Present', {
        templateId: template.id,
        withModeration: false,
        isFromDiscover: !template.isPrivate,
      });
      presentPublicLesson({
        method: 'present',
        isFromDiscover: true,
        template: {
          id: template.id,
          slides: template.slides,
          requireRealNames: template.requireRealNames,
        },
      });
    }
  };
  return (
    <>
      {isDesktop && (
        <DropDownButton
          menu={presentMenu({
            title: 'Preview',
            icon: isLoadingPreview ? <LoadingOutlined /> : undefined,
            description: 'Preview in your own pace with a host and participant view',
            onClick: () => {
              isDesktop &&
                !isLoadingPreview &&
                createPreviewGame({
                  templateId: template.id,
                  activeTenantId: tenantId,
                  onSuccessFunction: data => {
                    metrics.logEvent('Preview.Open.PreviewPage', {
                      templateId: template.id,
                      isFromDiscover: !template.isPrivate,
                    });
                    router.push(
                      `/${tenantId}/lessons/${data.templateId}/preview/${data.id}`,
                    );
                  },
                });
            },
          })}
          onClick={() => {
            handlePresent();
          }}
          style={{
            minWidth: '130px',
            maxWidth: '220px',
            display: 'flex',
          }}
          isLoading={isCreatingGame}
        >
          Start lesson
        </DropDownButton>
      )}

      {!isDesktop && (
        <Button
          icon={<PlaySvg />}
          styles={{
            margin: '0 10px 0 10px',
            width: '100%',
            fontSize: '12px',
          }}
        >
          Start lesson
        </Button>
      )}
      <Button
        styles={{
          margin: '0 10px 0 10px',
          fontSize: '12px',
        }}
        height="48px"
        type="secondary"
        onClick={e => {
          e.stopPropagation();
          handleEdit();
        }}
      >
        {isLoading ? <LoadingOutlined /> : t('Copy & Edit')}
      </Button>
    </>
  );
};

const NewTemplatePreview = ({
  authenticated,
  setShowShareModal,
  showShareModal,
}: {
  authenticated: boolean;
  setShowShareModal: (showShareModal: boolean) => void;
  showShareModal: boolean;
}) => {
  const { template, isError } = useContext(TemplateContext);

  const [selectedSlide, setSelectedSlide] = useState<Slide | undefined>();
  const { thumbnail: creatorThumbnail } = useTemplateCreatorPhoto({
    creatorThumbnailId: template?.creatorThumbnailId,
    isPrivate: !!template?.isPrivate, //todo check if this is correct
  });
  const { t } = useTranslation('Host');
  useEffect(() => {
    if (template && template.slides) {
      const selectedSlide = template.slides[0];
      setSelectedSlide(selectedSlide);
    }
  }, [template]);

  if (isError) return null;

  return (
    <>
      <MainContainer style={{ display: 'flex', flexDirection: 'column' }}>
        {template && (
          <BaseModal
            visible={showShareModal}
            onCancel={() => setShowShareModal(false)}
            title="Share this lesson with..."
          >
            <ShareGlobalLesson
              templateId={template.id}
              isGeneratedByAI={!!template?.metadata?.['aiTopic']}
            />
          </BaseModal>
        )}
        {template && (
          <LessonMediaPreFetcher
            slides={template?.slides}
            isDiscoveredTemplate
            isAuthenticated={authenticated}
            isOwner={false}
          />
        )}
        <TemplateInfoContainer>
          <ImageContainer style={{ borderRadius: 10, overflow: 'hidden' }}>
            {template && !template.isPrivate && template.coverImageId && (
              <CoverPhotoRenderer
                id={template.id}
                coverImageId={template.coverImageId}
                coverImageAltText={template.coverImageAltText}
              />
            )}
            {template && !template.coverImageId && (
              <img src={coverImage.src} alt="Curipod placeholder" />
            )}
          </ImageContainer>
          <div>
            <h1>{template?.title}</h1>
            {template && (
              <>
                <ProfileSection>
                  <Link
                    passHref
                    href={getDiscoverCreatorUrl(
                      template.creatorName,
                      template.creatorProfileId,
                    )}
                  >
                    <a>
                      <ProfileCircle $radius={40} $showHoverAnimation={true}>
                        {creatorThumbnail && (
                          <img
                            style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                            src={creatorThumbnail}
                            height={'10px'}
                            width={'100%'}
                            alt={`Profile picture of ${template.creatorName}`}
                          />
                        )}
                        {!creatorThumbnail && (
                          <Image
                            style={{ width: '100%', height: '100%', borderRadius: '50%' }}
                            src={coverImage}
                            height={'100%'}
                            width={'100%'}
                            alt={`Profile picture of ${template.creatorName}`}
                          />
                        )}
                      </ProfileCircle>
                    </a>
                  </Link>
                  <div>
                    <Link
                      passHref
                      href={getDiscoverCreatorUrl(
                        template.creatorName,
                        template.creatorProfileId,
                      )}
                    >
                      <a>
                        <h2>
                          <span>
                            <Tooltip title={t('Verified')}>
                              <IconWrapper height="16px" width="16px" color={'#ffa55b'}>
                                <ProfileChecked />
                              </IconWrapper>
                            </Tooltip>
                          </span>
                          {template.creatorName}
                        </h2>
                      </a>
                    </Link>
                    <p>
                      {t('UpdatedDaysAgo', {
                        defaultValue: 'Updated {{days}} ago',
                        days: dayjs(template.modifiedAt).fromNow(true),
                      })}
                    </p>
                  </div>
                </ProfileSection>
                <ButtonsContainer>
                  <AuthenticatedActions template={template} />
                  <StyledIconWrapper
                    height="25px"
                    width="25px"
                    style={{
                      marginLeft: 10,
                    }}
                    onClick={e => {
                      setShowShareModal(true);
                      e.stopPropagation();
                    }}
                  >
                    <ShareSvg /> Share
                  </StyledIconWrapper>
                </ButtonsContainer>
                <Divider
                  style={{ maxWidth: '300px', minWidth: '300px', width: '300px' }}
                />
                <LessonSavedContainer>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '20px',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper
                        height="14px"
                        width="14px"
                        color="#828282"
                        style={{ gap: 5, color: '#828282', cursor: 'default' }}
                      >
                        <PlaySvg /> Plays
                      </IconWrapper>
                      <span
                        aria-label="Plays"
                        style={{ fontWeight: 'bold', fontSize: 24 }}
                      >
                        {template.playCount}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper
                        height="16px"
                        width="16px"
                        color="#828282"
                        style={{ gap: 5, color: '#828282', cursor: 'default' }}
                      >
                        <DownloadSvg /> Copies
                      </IconWrapper>
                      <span
                        aria-label="Inspired"
                        style={{ fontWeight: 'bold', fontSize: 24 }}
                      >
                        {template.downloadCount}
                      </span>
                    </div>

                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                      }}
                    >
                      <IconWrapper
                        height="20px"
                        width="20px"
                        color="#828282"
                        style={{
                          gap: 5,
                          display: 'flex',
                          cursor: 'default',
                          color: '#828282',
                        }}
                      >
                        <GroupSvg /> Students
                      </IconWrapper>
                      <span
                        aria-label="Students"
                        style={{ fontWeight: 'bold', fontSize: 24 }}
                      >
                        {/*//TODO - Add student count {template.playCount} */}
                        --
                      </span>
                    </div>
                  </div>
                </LessonSavedContainer>
              </>
            )}
          </div>
          <h2>Content</h2>
          <h2>Preview</h2>
          {/* //TODO add full screen */}
          <div>
            <h3>Lesson description</h3>
            <p>{template ? template.description : ''}</p>
            <Divider />
          </div>
          <ContentContainer>
            <PreviewContainer
              id={PREVIEW_CONTAINER_ID}
              style={{ height: 'auto', width: '100%' }}
            >
              {template && selectedSlide && (
                <SlideWrapperViwer
                  aspect={{ height: 1080, width: 1920 }}
                  slide={selectedSlide}
                  backgroundMedia={selectedSlide.backgroundMedia}
                  resultView={false}
                  showStaticPoll={true}
                  hideLogo={false}
                  isAuthenticated={authenticated}
                  isDiscoverTemplate={true}
                >
                  <SlideWrapperOverlay>
                    <LiveGamePreviewActionButtons
                      slides={template.slides}
                      selectedSlide={selectedSlide}
                      setSelectedSlide={setSelectedSlide}
                    />
                  </SlideWrapperOverlay>
                </SlideWrapperViwer>
              )}
            </PreviewContainer>
          </ContentContainer>
        </TemplateInfoContainer>
        {/* <SEORenderedRounds /> */}
        {template && <SEOSuggestedContent template={template} />}
      </MainContainer>
    </>
  );
};

const SEOSuggestedContent = ({ template }: { template: PreviewTemplate }) => {
  const { state } = useDiscoverSearchQueryState();
  const { t } = useTranslation('Host');
  const metrics = useMetrics();

  const { items } = useTemplateSearch(
    state ? { ...state, searchPhrase: `${template.title}` } : undefined,
    [TEMPLATE_SUGGESTED_CONTENT_CACHE_KEY, template.id],
    'RelatedContent',
  );

  const handleCardClicked = useCallback(
    (templateId: string) => {
      metrics.logEvent('DiscoverPage.Template.Open', {
        templateId,
        query: 'suggested content',
        itemIndex: items?.pages
          .flatMap(page => page.items)
          .findIndex(item => item.id === templateId),
        totalItems: items ? items.pages[0].total : 0,
      });
    },
    [items, metrics],
  );

  return (
    <div style={{ position: 'relative', width: '100%', padding: '20px' }}>
      <h2 style={{ fontWeight: 'bold' }}>{t('Suggested content')}</h2>
      <SuggestedContentLessons>
        {items?.pages.flatMap(res =>
          res.items.map(item => {
            if (item.id === template.id) return null;
            return (
              <LessonCard
                key={item.id}
                link={getDiscoverLessonUrl({
                  templateName: item.title,
                  templateId: item.id,
                })}
                authorLink={getDiscoverCreatorUrl(
                  item.creatorName,
                  item.creatorProfileId,
                )}
                template={item}
                onClick={() => {
                  handleCardClicked(item.id);
                }}
              />
            );
          }),
        )}
      </SuggestedContentLessons>
    </div>
  );
};

const SuggestedContentLessons = styled.div`
  width: 100%;
  padding: 20px 0px;
  display: grid;
  position: relative;
  grid-template-columns: repeat(auto-fit, minmax(310px, 1fr));
  grid-auto-rows: 220px;
  gap: 20px;
`;

const ButtonsContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  font-weight: bold;
`;

const StyledIconWrapper = styled(IconWrapper)`
  cursor: pointer;
  font-weight: bold;
  text-decoration: underline;

  svg {
    margin-right: 5px;
  }

  &:hover {
    text-decoration: none;
  }
`;

const TemplatePage = () => {
  const { templateId } = useRouterMatch();

  const [showShareModal, setShowShareModal] = useState(false); //TODO
  const session = useSession();
  return (
    <>
      <NewAppLayout allowUnAuthenticated={true}>
        <PageContainer $isAuthenticated={session.status === 'authenticated'}>
          <LessonDataFetcher templateId={templateId}>
            <NewTemplatePreview
              authenticated={session.status === 'authenticated'}
              setShowShareModal={setShowShareModal}
              showShareModal={showShareModal}
            />
          </LessonDataFetcher>
          <NewLandingPageFooter footerBackgroundColor={theme.colors.paleBeige} />
        </PageContainer>
      </NewAppLayout>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async context => {
  const session = await getSession({ req: context.req });

  if (session) {
    const queryClient = new QueryClient();

    return {
      props: {
        ...(await provisionFlags(context, queryClient, session)),
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context)),
        session,
      },
    };
  } else {
    context.res.setHeader(
      'Cache-Control',
      'public, s-maxage=300, stale-while-revalidate=3600',
    );

    const queryClient = new QueryClient();

    const { templateId } = context.query as { templateId: string };
    const { seoTitle } = context.query as { seoTitle: string };

    await queryClient.prefetchQuery(
      ['indexed-template', templateId],
      async () => DiscoverService.findById(templateId),
      { staleTime: 1000 * 60 * 5 },
    );
    const q: DiscoverSearchQuery = {
      cursor: 0,
      limit: BASE_TEMPLATE_SEARCH_LIMIT,
      searchPhrase: seoTitle.replace('-', ' '),
    };
    await queryClient.prefetchInfiniteQuery(
      [TEMPLATE_SUGGESTED_CONTENT_CACHE_KEY, templateId],
      async ({ pageParam = 0 }: { pageParam?: number }) => {
        const res = await DiscoverService.getDiscoveredTemplates({
          query: {
            ...q,
            cursor: pageParam,
          },
          reddisCacheKey: [TEMPLATE_SUGGESTED_CONTENT_CACHE_KEY, templateId],
        });
        return res;
      },
      {
        getNextPageParam: lastPage => lastPage.cursor || null,
        staleTime: 60 * 60,
      },
    );

    return {
      props: {
        dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
        ...(await detectAndBestEffortSetLanguage(context)),
      },
    };
  }
};

export default TemplatePage;
